/**
 * The last dashboard reply per pour, in memory only, so reopening a pour on
 * a phone paints at once while the fresh reply loads. Keyed by user and
 * pour, so one user's data never shows for another; at most 10 pours (least
 * recently used dropped first); cleared on logout via mobileSessionCaches.
 */
import type { DashboardResponse, Pour2Limit } from '../services/pours';
import { registerMobileCacheClear } from './mobileSessionCaches';

export interface DashboardSummaryEntry {
  data: DashboardResponse;
  limits: Pour2Limit[];
  /** Epoch ms of the successful fetch. */
  fetchedAt: number;
}

const MAX_ENTRIES = 10;

const entries = new Map<string, DashboardSummaryEntry>();

const keyOf = (userId: string, pourId: string): string => `${userId}|${pourId}`;

/** The cached reply, or null. Without a user nothing is cached. */
export const readDashboardSummary = (
  userId: string | null | undefined,
  pourId: string,
): DashboardSummaryEntry | null => {
  if (!userId || !pourId) return null;
  const key = keyOf(userId, pourId);
  const entry = entries.get(key);
  if (!entry) return null;
  // Most recently used moves to the end (Map keeps insertion order).
  entries.delete(key);
  entries.set(key, entry);
  return entry;
};

export const writeDashboardSummary = (
  userId: string | null | undefined,
  pourId: string,
  entry: DashboardSummaryEntry,
): void => {
  if (!userId || !pourId) return;
  const key = keyOf(userId, pourId);
  entries.delete(key);
  entries.set(key, entry);
  while (entries.size > MAX_ENTRIES) {
    const oldest = entries.keys().next();
    if (oldest.done) break;
    entries.delete(oldest.value);
  }
};

export const clearDashboardSummaries = (): void => {
  entries.clear();
};

registerMobileCacheClear(clearDashboardSummaries);
